import Money from "../../Components/Money"
import { mapDesigns } from "@mono/mapDesigns"
import { allPieces } from "@/utils/data"

interface TradeEventCardProps {
  trade: ITrade
  status: TradeStatus
  situation: IMinifiedGameSituation
  constantData: ConstantDataOfHistory
}

const statusStyles: Record<string, string> = {
  offered: "bg-amber-400/10 border-amber-400/40 text-amber-300",
  accepted: "bg-emerald-400/10 border-emerald-400/40 text-emerald-300",
  declined: "bg-red-400/10 border-red-400/40 text-red-300",
}

const TradeEventCard: React.FC<TradeEventCardProps> = ({
  trade,
  status,
  situation,
  constantData,
}) => {
  const map = mapDesigns.find((m) => m.id === constantData.settings.mapId)

  const getPlayer = (order: PlayerOrder) => {
    const sitPlayer = situation.players.find((p) => p.order === order)
    const constPlayer = constantData.players.find(
      (p) => p.id === sitPlayer?.id
    )
    const user =
      constantData.users?.find((u) => u.email === sitPlayer?.id) ||
      (constPlayer as unknown as IUser | undefined)
    return {
      name: user?.username || sitPlayer?.id || `Player ${order}`,
      piece: allPieces.find((pc) => pc.id === constPlayer?.pieceId),
    }
  }

  const offering = getPlayer(trade.offeringPlayerOrder)
  const offered = getPlayer(trade.offeredPlayerOrder)

  const renderPlayerChip = (player: ReturnType<typeof getPlayer>) => (
    <div className="flex items-center gap-2 min-w-0">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-black/40 ring-1 ring-white/20">
        {player.piece?.imgSrc && (
          <img className="h-6 w-6 object-contain" src={player.piece.imgSrc} />
        )}
      </div>
      <span className="text-white font-semibold truncate">{player.name}</span>
    </div>
  )

  const renderProperty = (propertyOrder: number) => {
    const cell = map?.cells.find((c) => c.order === propertyOrder)
    const color = map?.groups.find(
      (g) => g.id === cell?.propertyDetails?.groupId
    )?.color
    return (
      <div
        key={propertyOrder}
        className="flex items-center gap-2 rounded-md bg-black/30 border border-gray-600 px-2 py-1"
      >
        <span
          className="h-3 w-3 shrink-0 rounded-full border border-black/40"
          style={{ backgroundColor: color || "#9ca3af" }}
        />
        <span className="text-white text-sm truncate">
          {cell?.propertyDetails?.name || `Property #${propertyOrder}`}
        </span>
      </div>
    )
  }

  // One side of the trade: everything a player hands over (properties + money).
  const renderSide = (title: string, properties: number[], money: number) => (
    <div className="flex-1 min-w-0 rounded-lg bg-gray-900/60 border border-gray-600 p-3">
      <div className="text-gray-400 text-xs uppercase tracking-wider mb-2">
        {title}
      </div>
      <div className="flex flex-col gap-1.5">
        {properties.map(renderProperty)}
        {money > 0 && (
          <Money
            money={money}
            className="text-emerald-300 font-semibold text-sm"
          />
        )}
        {properties.length === 0 && money <= 0 && (
          <span className="text-gray-500 text-sm italic">Nothing</span>
        )}
      </div>
    </div>
  )

  return (
    <div className="rounded-lg border border-amber-400/40 bg-gray-800/60 p-4">
      <div className="flex items-center gap-2 mb-3">
        <i className="fa-solid fa-right-left text-amber-400"></i>
        <span className="text-white font-semibold text-lg">Trade</span>
        <span
          className={`ml-auto rounded-full border px-2 py-0.5 text-xs font-bold uppercase tracking-wide ${
            statusStyles[status] || statusStyles.offered
          }`}
        >
          {status}
        </span>
      </div>

      <div className="flex items-center gap-3 mb-4">
        {renderPlayerChip(offering)}
        <i className="fa-solid fa-arrow-right-long text-amber-300 shrink-0"></i>
        {renderPlayerChip(offered)}
      </div>

      <div className="flex gap-3">
        {renderSide(
          `${offering.name} gives`,
          trade.givenProperties,
          trade.moneyGiven
        )}
        {renderSide(
          `${offered.name} gives`,
          trade.takenProperties,
          trade.moneyTaken
        )}
      </div>
    </div>
  )
}

export default TradeEventCard
